import React, {useCallback, useContext, useMemo, useState} from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import PropTypes from "prop-types";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faEdit, faPlus} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Hooks
import useApi from "../../hooks/useApi.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Utils
import {getFields} from "../../utils/builder.js";
import {convertToUserTimezone} from "../../utils/helpers.js";
import {
  getReviewConditionItemLabel,
  REVIEW_ALLOWED_TRIGGERS,
  REVIEW_WEATHER_DEPENDS_OPTIONS,
  REVIEW_WEATHER_FIELDS,
  toManageConditionalExisting,
  transformManageConditionalSubmission
} from "./reviewConditions.js";

// Components
import ModalReview from "./ModalReview.js";
import Modal from "../../components/Modal.js";
import InputText from "../../components/form/InputText.js";
import InputTextArea from "../../components/form/InputTextArea.js";
import InputNumber from "../../components/form/InputNumber.js";
import SearchSelect from "../../components/SearchSelect.js";
import InputDate from "../../components/form/InputDate.js";
import InputCheck from "../../components/form/InputCheck.js";
import InputError from "../../components/form/InputError.js";
import InputRadioGroup from "../../components/form/InputRadioGroup.js";
import ManageConditional from "../checksheet-builder/ManageConditional.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {Button, Heading, Inline, Label, Text} from "../../style/components/general.js";

const defaultValues = {
  description: "",
  limit: 1,
  hasCondition: false,
  condition: {operator: "or", list: []}
};

const schema = yup.object().shape({
  description: yup.string().required("Please provide a description for review."),
  hasFields: yup.boolean(),
  fields: yup
    .mixed()
    .nullable()
    .when("hasFields", {
      is: true,
      then: () =>
        yup.array().test({
          test: val => Array.isArray(val) && val.length > 0,
          message: "Please select at least one field."
        })
    }),
  limit: yup
    .number()
    .nullable()
    .when("hasFields", {
      is: true,
      then: () => yup.number().typeError("Please provide a limit.")
    }),
  hasEnd: yup.boolean(),
  end: yup
    .mixed()
    .nullable()
    .when("hasEnd", {
      is: true,
      then: () => yup.date().typeError("Please provide a date when the review will expire.")
    }),
  hasCondition: yup.boolean(),
  condition: yup
    .mixed()
    .nullable()
    .when("hasCondition", {
      is: true,
      then: () =>
        yup.object().test({
          test: val => Array.isArray(val?.list) && val.list.length > 0,
          message: "Please add at least one condition."
        })
    })
});

const ModalReviewManage = ({visible, setVisible, goBack, checksheet, review, reload}) => {
  const {api: apiReviews} = useApi("checksheet-reviews");

  const {facility} = useContext(FacilityNavContext);

  const hasPrimaryAddress = !!facility?.primaryAddress;

  const [showModalReview, setShowModalReview] = useState(false);
  const [results, setResults] = useState([]);
  const [openManage, setOpenManage] = useState(!review?.data?.condition?.list?.length);
  const [editingCondition, setEditingCondition] = useState(null);
  const [editingConditionIdx, setEditingConditionIdx] = useState(null);

  const formDefaultValues = (() => {
    if (!review?.data) return defaultValues;
    const end =
      review.endAfter != null
        ? dayjs(convertToUserTimezone(review.endAfter)).format("YYYY-MM-DD")
        : null;
    return {
      name: review.name,
      hasFields: review.data.fields?.length > 0,
      ...review.data,
      hasCondition: !!review.data.condition?.list?.length,
      condition: review.data.condition || {operator: "or", list: []},
      hasEnd: review.endAfter !== null,
      end
    };
  })();

  const form = useForm({
    defaultValues: formDefaultValues,
    resolver: yupResolver(schema)
  });
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: {errors},
    reset
  } = form;

  const values = watch();
  const watchHasEnd = watch("hasEnd");
  const watchHasFields = watch("hasFields");
  const watchHasCondition = watch("hasCondition");
  const watchCondition = watch("condition") || {operator: "or", list: []};

  const {fields, append, remove} = useFieldArray({control, name: "fields"});

  const mergeBuilders = useCallback(
    builder => {
      const {allIds: taskAllIds, byId: taskById} = builder;
      const {byId: facilityById} = facility?.builder || {};

      const consolidated = {allIds: taskAllIds, byId: {}};
      Object.values(taskById).map(attributes => {
        const {name, help, condition, children, deleted} = attributes;
        // Consolidate task/facility attributes for groups/components
        if (facility && name in facilityById) {
          const taskHelp = help || [];
          const facilityHelp = facilityById[name]?.help || [];
          const facilityDeleted = facilityById[name]?.deleted;
          const facilityCondition = facilityById[name]?.condition;
          const consolidatedHelp = [...facilityHelp, ...taskHelp];
          const consolidatedElement = {
            ...facilityById[name],
            condition: facilityCondition || condition,
            children,
            deleted: facilityDeleted || deleted
          };

          if (consolidatedHelp?.length > 0) consolidatedElement.help = consolidatedHelp;

          consolidated.byId[name] = consolidatedElement;
        } else consolidated.byId[name] = attributes;
      });

      return consolidated;
    },
    [facility]
  );

  const defaultBuilder = useMemo(
    () =>
      checksheet?.default?.builder
        ? mergeBuilders(checksheet.default.builder)
        : {byId: {}, allIds: []},
    [checksheet, mergeBuilders]
  );

  const stateBuilder = useMemo(
    () => (checksheet?.state ? mergeBuilders(checksheet.state.builder) : undefined),
    [checksheet, mergeBuilders]
  );

  // Builder passed to ManageConditional (weather depends / field triggers).
  const consolidatedBuilder = useMemo(() => {
    const sourceBuilder = checksheet?.default?.builder || checksheet?.builder;
    if (!sourceBuilder) return {byId: {}, allIds: []};
    return mergeBuilders(sourceBuilder);
  }, [checksheet, mergeBuilders]);

  const allFields = useMemo(() => {
    const defaultFields = getFields(defaultBuilder.allIds, defaultBuilder.byId);
    const stateFields = stateBuilder ? getFields(stateBuilder.allIds, stateBuilder.byId) : [];

    const defaultOptions = defaultFields
      .filter(option => {
        const match = stateFields.find(({name}) => name === option.name);

        return (!match || match.deleted) && !option.deleted;
      })
      .map(option => ({
        ...option,
        ancestry: `${option.ancestry} (DEFAULT ONLY)`
      }));

    const bothOptions = defaultFields.filter(option => {
      const match = stateFields.find(({name}) => name === option.name);

      return match && !match.deleted && !option.deleted;
    });

    const stateOptions = stateFields
      .filter(option => {
        const match = defaultFields.find(({name}) => name === option.name);

        return (!match || match.deleted) && !option.deleted;
      })
      .map(option => ({
        ...option,
        ancestry: `${option.ancestry} (${
          checksheet.state?.state?.name ? checksheet.state.state.name.toUpperCase() : "STATE"
        } ONLY)`
      }));

    return [...bothOptions, ...defaultOptions, ...stateOptions];
  }, [defaultBuilder, stateBuilder, checksheet]);

  const weatherFieldOptions = useMemo(
    () => (hasPrimaryAddress ? REVIEW_WEATHER_FIELDS : []),
    [hasPrimaryAddress]
  );

  const search = query => {
    const fieldResults = (() => {
      if (query) {
        const lower = query.toLowerCase();
        const tempResults = allFields.filter(field => field.ancestry.toLowerCase().includes(lower));
        tempResults.sort(
          (a, b) => a.label.toLowerCase().indexOf(lower) - b.ancestry.toLowerCase().indexOf(lower)
        );
        return tempResults.map(field => ({name: field.ancestry, value: field.name}));
      }
      return allFields.map(field => ({name: field.ancestry, value: field.name}));
    })();

    const weatherResults = query
      ? weatherFieldOptions.filter(({name}) => name.toLowerCase().includes(query.toLowerCase()))
      : weatherFieldOptions;

    // Weather pseudo-fields at the bottom of SearchSelect results.
    setResults([...fieldResults, ...weatherResults]);
  };

  const handleConditionSubmission = raw => {
    const item = transformManageConditionalSubmission(raw);
    const list = [...(watchCondition.list || [])];
    if (editingConditionIdx || editingConditionIdx === 0) list.splice(editingConditionIdx, 1, item);
    else list.push(item);
    setValue(
      "condition",
      {operator: watchCondition.operator || "or", list},
      {shouldValidate: true}
    );
    setEditingCondition(null);
    setEditingConditionIdx(null);
    setOpenManage(false);
  };

  const handleEditCondition = (item, idx) => {
    setEditingCondition(toManageConditionalExisting(item));
    setEditingConditionIdx(idx);
    setOpenManage(true);
  };

  const clearEditingCondition = () => {
    setEditingCondition(null);
    setEditingConditionIdx(null);
  };

  const removeCondition = idx => {
    const list = [...(watchCondition.list || [])];
    list.splice(idx, 1);
    setValue("condition", {...watchCondition, list}, {shouldValidate: true});
    if (list.length === 0) {
      clearEditingCondition();
      setOpenManage(true);
    } else if (editingConditionIdx === idx) {
      clearEditingCondition();
      setOpenManage(false);
    } else if (editingConditionIdx != null && editingConditionIdx > idx) {
      setEditingConditionIdx(editingConditionIdx - 1);
    }
  };

  const handleSave = ({
    name,
    description,
    limit,
    fields: targetFields,
    end,
    hasEnd,
    hasCondition,
    condition
  }) => {
    const data = {description, limit, fields: targetFields};
    if (hasCondition && condition?.list?.length)
      data.condition = {operator: condition.operator || "or", list: condition.list};

    const params = {
      facilityChecksheetId: checksheet.id,
      name,
      data
    };

    if (hasEnd) params.endAfter = end;

    const success = !review ? 201 : 200;
    const request = !review ? apiReviews.callPost(params) : apiReviews.callPut(review.id, params);

    request.then(({status}) => {
      if (status === success) {
        goBack();
        reset(defaultValues);
        reload();
      }
    });
  };

  if (showModalReview)
    return (
      <ModalReview
        visible={visible}
        setVisible={setVisible}
        goBack={() => setShowModalReview(false)}
        checksheet={checksheet}
        preview={values}
      />
    );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton
      goBack={() => {
        goBack();
        reset(defaultValues);
      }}>
      <Heading>{review ? "Edit" : "Add"} Required Review</Heading>

      <FormProvider {...form}>
        {/*
          No wrapping <Form>: ManageConditional renders its own <form>, and nested
          forms are invalid HTML (browser submits the outer Save instead of staging).
        */}
        <InputText name="name" label="Title" placeholder="Title..." required />

        <InputTextArea
          name="description"
          label="Description"
          placeholder="Please review and acknowledge..."
          required
        />

        <InputCheck name="hasFields">Add fields to review?</InputCheck>
        {watchHasFields && (
          <>
            <SearchWrapper>
              <SearchSelect
                label="Fields *"
                results={results}
                setResults={setResults}
                search={search}
                add={field => {
                  if (fields?.filter(({value}) => value === field.value).length === 0)
                    append(field);
                }}
                showAll
              />
              <Selections>
                {fields?.map((field, idx) => (
                  <Selection key={field.value} type="button" onClick={() => remove(idx)}>
                    {field.name}&nbsp;
                    <FontAwesomeIcon icon={faClose} />
                  </Selection>
                ))}
              </Selections>
              <InputError name="fields" errors={errors} />
            </SearchWrapper>
            <InputNumber name="limit" label="Data from last" units="Records" required />
          </>
        )}

        <InputCheck name="hasCondition">Show conditionally?</InputCheck>
        {watchHasCondition && (
          <ConditionBlock>
            {(watchCondition.list || []).length > 0 && (
              <>
                <Label bold>Conditions</Label>
                {(watchCondition.list || []).map((item, idx) => {
                  if (idx === editingConditionIdx) return null;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <ConditionRow key={`${item.depends}-${idx}`}>
                      <Text>{getReviewConditionItemLabel(item)}</Text>
                      <Inline>
                        <Button
                          type="button"
                          title="Edit"
                          onClick={() => handleEditCondition(item, idx)}
                          data-testid="reviewCondition.edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          type="button"
                          title="Delete"
                          onClick={() => removeCondition(idx)}
                          data-testid="reviewCondition.delete">
                          <FontAwesomeIcon icon={faClose} />
                        </Button>
                      </Inline>
                    </ConditionRow>
                  );
                })}
                {(watchCondition.list || []).length > 1 && (
                  <InputRadioGroup
                    name="condition.operator"
                    label="Logical Operator"
                    options={[{option: "or"}, {option: "and"}]}
                    defaultValue={watchCondition.operator || "or"}
                  />
                )}
              </>
            )}
            {openManage ? (
              <>
                {(watchCondition.list || []).length > 0 && editingConditionIdx == null && <hr />}
                <ManageConditional
                  key={
                    editingConditionIdx != null ? `edit-${editingConditionIdx}` : "create-condition"
                  }
                  builder={consolidatedBuilder}
                  submission={handleConditionSubmission}
                  existing={editingCondition}
                  setExisting={setEditingCondition}
                  open={open => {
                    setOpenManage(open);
                    if (!open) clearEditingCondition();
                  }}
                  canCancel={(watchCondition.list || []).length > 0}
                  hasPrimaryAddress={hasPrimaryAddress}
                  allowedTriggers={REVIEW_ALLOWED_TRIGGERS}
                  weatherDependsOptions={REVIEW_WEATHER_DEPENDS_OPTIONS}
                />
              </>
            ) : (
              <Button
                type="button"
                onClick={() => {
                  clearEditingCondition();
                  setOpenManage(true);
                }}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
          </ConditionBlock>
        )}

        <InputCheck name="hasEnd">Expire on date?</InputCheck>
        {watchHasEnd && (
          <InputDate
            name="end"
            label="Expires"
            required={watchHasEnd}
            min={dayjs().format("YYYY-MM-DD")}
          />
        )}

        <Submit>
          <Button type="button" onClick={handleSubmit(handleSave)}>
            Save
          </Button>
          <Button type="button" onClick={() => setShowModalReview(true)}>
            Preview
          </Button>
        </Submit>
      </FormProvider>
    </Modal>
  );
};

ModalReviewManage.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  review: PropTypes.objectOf(PropTypes.any),
  reload: PropTypes.func.isRequired
};

ModalReviewManage.defaultProps = {
  review: undefined
};

// Style Overrides
const SearchWrapper = styled.div`
  position: relative;
  margin: ${pad}px 0;
  width: 100%;
`;

const Selections = styled.div`
  position: relative;
  ${flex("row", "wrap", "start", "start")};
`;

const Selection = styled(Button)`
  padding: ${pad / 2}px;
  width: fit-content;
  margin: ${pad}px ${pad / 2}px 0 0;
  ${voice.quiet};
`;

const ConditionBlock = styled.div`
  width: 100%;
  margin: ${pad}px 0;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
`;

const ConditionRow = styled(Inline)`
  width: 100%;
  justify-content: space-between;
  margin: ${pad / 2}px 0;
`;

const Submit = styled(Inline)`
  margin-top: ${pad * 2}px;
`;

export default ModalReviewManage;
